import type { ViewBox } from '@view-model/domain/view-model';
import type { ScaleExtent } from './domain/ScaleExtent';
import { MousePointerDetector } from './MousePointerDetector';
import type { SVGZoomPanHandlerRef } from './SVGZoomPanHandler';
import { SVGZoomPanHandler } from './SVGZoomPanHandler';
import type { RefObject } from 'react';
import type { GetGuardProps } from '@model-framework/drag-file/useDragFileGuard';
import type { Rect } from '@view-model/models/common/basic/Rect';
import { VIEW_MODEL_CANVAS_SIZE } from 'balus-common';
type Props = {
  children: React.ReactNode;
  svgZoomPanHandlerRef: RefObject<SVGZoomPanHandlerRef | null>;
  viewModelSVGRef: React.RefObject<SVGSVGElement | null>;
  scaleExtent: ScaleExtent;
  viewBox: ViewBox;
  deselectElements: () => void;
  onMousePointerDetected: (x: number, y: number) => void;
  getGuardProps: GetGuardProps;
  onRectSelection: () => void;
  onRectSelectionEnd: (rect: Rect) => void;
};

const GRID_CELL_SIZE = 512;
const GRID_STROKE_WIDTH = 3;

export const GridArea: React.FC<Props> = (props: Props) => {
  const {
    children,
    svgZoomPanHandlerRef,
    viewModelSVGRef,
    scaleExtent,
    viewBox,
    deselectElements,
    onMousePointerDetected,
    getGuardProps,
    onRectSelection,
    onRectSelectionEnd,
  } = props;

  return (
    <SVGZoomPanHandler
      ref={svgZoomPanHandlerRef}
      viewModelSVGRef={viewModelSVGRef}
      scaleExtent={scaleExtent}
      viewBox={viewBox}
    >
      <MousePointerDetector
        onMousePointerDetected={onMousePointerDetected}
        onRectSelection={onRectSelection}
        onRectSelectionEnd={onRectSelectionEnd}
      >
        <rect
          className="cursor-grab fill-neutral-200 active:cursor-grabbing"
          width={VIEW_MODEL_CANVAS_SIZE}
          height={VIEW_MODEL_CANVAS_SIZE}
          transform={`translate(${-VIEW_MODEL_CANVAS_SIZE / 2}, ${-VIEW_MODEL_CANVAS_SIZE / 2})`}
          onClick={deselectElements}
          {...getGuardProps()}
        />
        <GridPattern />
        {children}
      </MousePointerDetector>
    </SVGZoomPanHandler>
  );
};

/**
 * Grid（格子）の描画
 */
const GridPattern: React.FC<unknown> = () => {
  return (
    <g pointerEvents="none" opacity={1}>
      <defs>
        <pattern
          id="small-grid"
          patternUnits="userSpaceOnUse"
          viewBox={`0 0 ${GRID_CELL_SIZE} ${GRID_CELL_SIZE}`}
          x="0"
          y="0"
          width={GRID_CELL_SIZE}
          height={GRID_CELL_SIZE}
        >
          <rect width={GRID_CELL_SIZE} height={GRID_CELL_SIZE} style={{ fill: 'transparent' }} />
          <line className="stroke-gray-700" x1="0" y1="0" x2={GRID_CELL_SIZE} y2="0" strokeWidth={GRID_STROKE_WIDTH} />
          <line className="stroke-gray-700" x1="0" y1="0" x2="0" y2={GRID_CELL_SIZE} strokeWidth={GRID_STROKE_WIDTH} />
        </pattern>
      </defs>
      <rect
        width={VIEW_MODEL_CANVAS_SIZE}
        height={VIEW_MODEL_CANVAS_SIZE}
        transform={`translate(${-VIEW_MODEL_CANVAS_SIZE / 2}, ${-VIEW_MODEL_CANVAS_SIZE / 2})`}
        style={{ fill: 'url(#small-grid)', stroke: 'none' }}
      />
      {/* Chrome最新版（89）でd3のズームが効かなくなった問題への対応（Chromeやd3の対応によって不要になるかも）
            d3 が rect 要素からのイベントを拾えなくなってしまったようなので透明なdivを置くことでd3がイベントを送れるようにする
            https://docs.google.com/document/d/1U2_GlDOYjCiogsfda6IXkkC3HsD_9CJSs7TXJx7yfpY/edit#heading=h.tnexj6jyzd9l
            */}
      <g transform={`translate(${-VIEW_MODEL_CANVAS_SIZE / 2}, ${-VIEW_MODEL_CANVAS_SIZE / 2})`}>
        <foreignObject width={VIEW_MODEL_CANVAS_SIZE} height={VIEW_MODEL_CANVAS_SIZE}>
          <div className="size-full" />
        </foreignObject>
      </g>
    </g>
  );
};
